import { Controller } from "@hotwired/stimulus"

// Drives the in-class projector screen. Responses arrive over a Turbo Stream
// (see Exercise::InClassProjector::ResponsesComponent), so all this handles is the access
// code lifecycle and the instructor toggles (responses, names, and the
// tally's correct answer).
export default class extends Controller {
  static values = { url: String }
  static targets = ["codeDisplay", "countdown", "accessCode", "toggleButton", "answers", "namesButton", "responsesButton", "answerButton"]

  connect() {
    this.accessOpen = true
    this.responsesVisible = false
    this.namesVisible = false
    this.answerVisible = false
    this.secondsUntilRefresh = 60
    this.startTimer()

    // The Q1/Q2 pills are full navigations, so the controller reconnects on
    // every question switch. Access is the one toggle that must survive that —
    // otherwise switching questions puts the code back on the wall. (The
    // others resetting is right: a new question starts hidden.)
    if (this.storedAccessClosed()) this.setAccess(false, { refresh: false })

    // Turbo applies stream actions synchronously, so a new response pops in and
    // shoves the thread down in a single frame. Wrapping the render in a view
    // transition lets the browser tween it instead. Scoped to this page by
    // adding/removing the listener with the controller.
    this.smoothStreamRender = this.smoothStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.smoothStreamRender)
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
    document.removeEventListener("turbo:before-stream-render", this.smoothStreamRender)
  }

  smoothStreamRender(event) {
    // The broadcast re-renders the whole response region in one action. Each
    // bubble inside carries a stable view-transition-name, so the browser still
    // works out what actually moved and tweens only that.
    const target = event.target.getAttribute("target") || ""
    if (!target.startsWith("projector-responses")) return
    if (!document.startViewTransition) return // Firefox < 144, older Safari
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const originalRender = event.detail.render
    event.detail.render = (streamElement) =>
      document.startViewTransition(() => originalRender(streamElement))
  }

  startTimer() {
    this.timer = setInterval(() => {
      this.secondsUntilRefresh--

      if (this.secondsUntilRefresh <= 0) {
        this.refreshCode()
        this.secondsUntilRefresh = 60
      }

      this.updateDisplay()
    }, 1000)
  }

  updateDisplay() {
    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = `Next code in ${this.secondsUntilRefresh}s`
    }
    if (this.hasAccessCodeTarget) {
      this.accessCodeTarget.classList.toggle("is-expiring", this.secondsUntilRefresh <= 10)
    }
  }

  toggleAccess() {
    this.setAccess(!this.accessOpen)
  }

  setAccess(open, { refresh = true } = {}) {
    this.accessOpen = open
    this.storeAccessClosed(!open)

    if (this.hasCodeDisplayTarget) {
      this.codeDisplayTarget.classList.toggle("d-none", !this.accessOpen)
    }

    if (this.accessOpen && refresh) {
      this.refreshCode()
      this.secondsUntilRefresh = 60
    }

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.textContent = this.accessOpen ? "Close Access" : "Reopen Access"
      this.toggleButtonTarget.classList.toggle("btn-warning", !this.accessOpen)
      this.toggleButtonTarget.classList.toggle("btn-outline-secondary", this.accessOpen)
    }
  }

  // sessionStorage, not localStorage: scoped to this tab (this screen), and a
  // fresh tab starts open. Keyed by the refresh URL, which carries the problem
  // set id. Storage can throw (private mode, blocked site data) — then access
  // just resets on navigation, as it did before.
  accessStorageKey() {
    return `in-class-projector:access-closed:${this.urlValue}`
  }

  storedAccessClosed() {
    try { return sessionStorage.getItem(this.accessStorageKey()) === "1" } catch (e) { return false }
  }

  storeAccessClosed(closed) {
    try {
      if (closed) sessionStorage.setItem(this.accessStorageKey(), "1")
      else sessionStorage.removeItem(this.accessStorageKey())
    } catch (e) { /* see accessStorageKey */ }
  }

  // Presentation state for THIS screen only, so it's plain CSS toggling — no
  // Turbo frame/stream round-trip. The hidden region stays in the DOM, which
  // keeps the stream subscription connected and broadcasts landing, so the
  // reveal is instant and already up to date. The meter stays visible either
  // way: the instructor watches "N of M answered" to decide when to reveal.
  toggleResponses() {
    this.responsesVisible = !this.responsesVisible

    if (this.hasAnswersTarget) {
      this.answersTarget.classList.toggle("responses-hidden", !this.responsesVisible)
    }

    if (this.hasResponsesButtonTarget) {
      this.responsesButtonTarget.textContent = this.responsesVisible ? "Hide Responses" : "Show Responses"
    }

    if (this.hasNamesButtonTarget) {
      this.namesButtonTarget.classList.toggle("d-none", !this.responsesVisible)
    }

    if (this.hasAnswerButtonTarget) {
      this.answerButtonTarget.classList.toggle("d-none", !this.responsesVisible)
    }
  }

  // Same shape as the names toggle: the correct choice is always marked in the
  // DOM, and a class on the answers target decides whether it's highlighted —
  // so a vote landing after the reveal keeps the highlight.
  toggleAnswer() {
    this.answerVisible = !this.answerVisible

    if (this.hasAnswersTarget) {
      this.answersTarget.classList.toggle("answer-hidden", !this.answerVisible)
    }

    if (this.hasAnswerButtonTarget) {
      this.answerButtonTarget.textContent = this.answerVisible ? "Hide Answer" : "Show Answer"
    }
  }

  toggleNames() {
    this.namesVisible = !this.namesVisible

    if (this.hasAnswersTarget) {
      this.answersTarget.classList.toggle("names-hidden", !this.namesVisible)
    }

    if (this.hasNamesButtonTarget) {
      this.namesButtonTarget.textContent = this.namesVisible ? "Hide Names" : "Show Names"
    }
  }

  async refreshCode() {
    if (!this.accessOpen) return

    try {
      const response = await fetch(this.urlValue, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
      if (response.ok) {
        const html = await response.text()
        if (this.hasCodeDisplayTarget) {
          this.codeDisplayTarget.innerHTML = html
          this.updateDisplay()
        }
      }
    } catch (e) {
      // silently retry on next tick
    }
  }
}
